import React from 'react';

import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

interface MoleculeFormFieldBannersProps {
  /** Whether we are creating a new schema (as opposed to editing an existing one). */
  isCreating: boolean;
  /** Whether the Molecule Form SSI is currently selected. */
  isMoleculeFormSelected: boolean;
  /** Whether the field list already contains a Base Molecule Link parent link field. */
  hasCompoundField: boolean;
  /** The number of available (untaken, non-archived) Molecule Compound schemas. */
  availableCompoundSchemaCount: number;
}

/**
 * Renders contextual banners for the Molecule Form schema creation flow:
 * - An info banner when the Compound field was successfully auto-populated.
 * - An error banner when no Molecule Compound schemas are available to link to.
 *
 * Returns null when neither banner applies (e.g., editing an existing schema,
 * Molecule Form not selected, or available schemas exist but field wasn't yet populated).
 */
export default function MoleculeFormFieldBanners({
  isCreating,
  isMoleculeFormSelected,
  hasCompoundField,
  availableCompoundSchemaCount,
}: MoleculeFormFieldBannersProps): JSX.Element | null {
  if (!isCreating || !isMoleculeFormSelected) {
    return null;
  }

  if (hasCompoundField) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text="This form schema has a parent link field to a Molecule Compound schema. You can change the base compound schema using the Base Molecule Link dropdown - only Compound schemas not already linked to another form schema will appear. Make sure the Parent Link box remains checked and validation is set to Require value."
      />
    );
  }

  if (availableCompoundSchemaCount === 0) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="No Molecule Compound schemas are available. Every Molecule Form schema requires a parent link to a Molecule Compound schema, but all existing Compound schemas are already linked to other Form schemas (or none have been created yet). Create a new Molecule Compound schema first, then create a Form schema."
      />
    );
  }

  return null;
}
